import React from 'react';
import { RotateCcw, PlayCircle, StopCircle, Github, Linkedin, Globe } from 'lucide-react';
import { Button } from './ui/Button';
import { ThemeDropdown } from './ui/ThemeDropdown';
import { useTheme } from '../contexts/ThemeContext';
import { useDemo } from '../contexts/DemoContext';

interface HeaderProps {
  onClearSession: () => void;
  sessionTopic?: string | null;
  congressSessions?: number[] | null;
}

export const Header: React.FC<HeaderProps> = ({ onClearSession, sessionTopic, congressSessions }) => {
  const { theme } = useTheme();
  const { isDemoMode, toggleDemoMode } = useDemo();
  
  // Helper function to format congress sessions
  const formatCongressSessions = (sessions: number[] | null): string => {
    if (!sessions || sessions.length === 0) return '';
    
    if (sessions.length === 1) {
      return `Congress ${sessions[0]}`;
    } else if (sessions.length <= 3) {
      return `Congress ${sessions.join(', ')}`;
    } else {
      return `Congress ${sessions[0]}-${sessions[sessions.length - 1]}`;
    }
  };

  const congressText = formatCongressSessions(congressSessions || null);
  
  return (
    <header className="theme-bg-sidebar backdrop-blur-sm border-b theme-border-sidebar px-4 md:px-4 lg:px-6 py-3 md:py-3 lg:py-4 relative z-50">
      <div className="w-full flex items-center justify-between">
        {/* Left side - Logo and branding */}
        <div className="flex items-center space-x-3 md:space-x-3 lg:space-x-4 flex-shrink-0 min-w-0">
          <div className="flex items-center space-x-2 md:space-x-2 lg:space-x-3">
            {/* Politheon Logo */}
            <img 
              src={theme === 'light' ? '/politheon-logo.png' : '/politheon-logo-light.png'}
              alt="Politheon Logo" 
              className="w-8 h-8 md:w-10 md:h-10 object-contain flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold theme-text-primary flex items-center gap-1 md:gap-2 flex-wrap">
                <span className="truncate">Politheon</span>
                <span className="theme-accent-yellow flex-shrink-0 hidden sm:inline">Policy AI</span>
                {isDemoMode && (
                  <span className="bg-orange-500/20 backdrop-blur-sm px-1.5 md:px-2 py-0.5 md:py-1 rounded-md border border-orange-400/30 text-xs font-bold text-orange-400 flex-shrink-0">
                    DEMO
                  </span>
                )}
              </h1>
              <p className="text-xs md:text-sm theme-text-muted hidden sm:block">
                AI-powered legislative intelligence
              </p>
            </div>
          </div>
          
          {/* Session info - only show on larger screens */}
          {sessionTopic && (
            <div className="hidden xl:flex items-center space-x-3">
              <div className="bg-blue-500/20 backdrop-blur-sm px-3 py-1 rounded-full border border-blue-400/30">
                <span className="text-sm font-medium theme-text-muted">
                  Current Topic: {sessionTopic}
                </span>
              </div>
              {congressText && (
                <div className="bg-emerald-500/20 backdrop-blur-sm px-3 py-1 rounded-full border border-emerald-400/30">
                  <span className="text-sm font-medium theme-text-muted">
                    {congressText}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Right side - Actions */}
        <div className="flex items-center space-x-2 md:space-x-2 lg:space-x-3 flex-shrink-0">
          {/* New Session Button - hide text on small/medium screens */}
          <Button
            variant="secondary"
            size="sm"
            onClick={onClearSession}
            className="flex items-center space-x-1 md:space-x-2 theme-bg-sidebar-hover theme-border-sidebar theme-text-secondary hover:theme-bg-tertiary hover:theme-accent-yellow transition-all duration-200"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden lg:inline">New Session</span>
          </Button>

          {/* Demo Mode Button - hide text on small/medium screens */}
          <Button
            variant="secondary"
            size="sm"
            onClick={toggleDemoMode}
            className={`flex items-center space-x-1 md:space-x-2 transition-all duration-200 ${
              isDemoMode 
                ? 'bg-orange-500/20 border-orange-400/30 text-orange-400 hover:bg-orange-500/30' 
                : 'theme-bg-sidebar-hover theme-border-sidebar theme-text-secondary hover:theme-bg-tertiary hover:theme-accent-yellow'
            }`}
            title={isDemoMode ? 'Exit Demo Mode' : 'Enter Demo Mode'}
          >
            {isDemoMode ? <StopCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
            <span className="hidden lg:inline">{isDemoMode ? 'Exit Demo' : 'Demo Mode'}</span>
          </Button>
          
          {/* Theme Dropdown - always visible */}
          <ThemeDropdown />
          
          {/* Social links - only visible on desktop */}
          <div className="hidden lg:flex items-center space-x-2">
            <a
              href="https://www.linkedin.com/company/politheon/"
              target="_blank"
              rel="noopener noreferrer"
              className="theme-text-muted hover:text-blue-400 transition-colors duration-200 p-1.5 rounded-lg hover:theme-bg-sidebar-hover"
              title="Follow us on LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            
            <a
              href="https://github.com/Politheon-HQ"
              target="_blank"
              rel="noopener noreferrer"
              className="theme-text-muted hover:theme-text-primary transition-colors duration-200 p-1.5 rounded-lg hover:theme-bg-sidebar-hover"
              title="View our GitHub"
            >
              <Github className="w-4 h-4" />
            </a>

            <a
              href="https://politheon.com"
              target="_blank"
              rel="noopener noreferrer"
              className="theme-text-muted hover:text-emerald-400 transition-colors duration-200 p-1.5 rounded-lg hover:theme-bg-sidebar-hover"
              title="Visit our Website"
            >
              <Globe className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};